import { resolveId } from '@medplum/core'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import type { Order } from '@seen/core/models/orders/Order'
import { getReferenceString } from '@seen/core/references'
import { compact, uniqBy } from 'lodash-es'
import { useMemo } from 'react'
import { useSearch } from '#src/hooks/useSearch.ts'

export interface OrderDocumentReferences {
  explicit: ExtendedDocumentReference[]
  suggested: ExtendedDocumentReference[]
}

export const useOrderDocumentReferences = (
  order: Order
): OrderDocumentReferences => {
  const { data: documentReferences } = useSearch(
    ExtendedDocumentReference,
    ExtendedDocumentReference.byRelated(order.reference)
  )

  const { data: diagnosticReportDocumentReferences = [] } = useSearch(
    ExtendedDocumentReference,
    {
      '_has:Observation:derived-from:_has:Observation:has-member:_has:DiagnosticReport:result:based-on':
        order.reference,
    }
  )

  const supportingInfoDocumentReferenceIds =
    order.documentReferences.map(resolveId)
  const { data: supportingInfoDocumentReferences } = useSearch(
    ExtendedDocumentReference,
    {
      _id: supportingInfoDocumentReferenceIds,
    },
    {
      query: {
        enabled: !!supportingInfoDocumentReferenceIds.length,
      },
    }
  )

  // Fetch documents that have predicted relations to this order
  const orderReferenceString = getReferenceString(order.reference)
  const { data: predictedDocumentReferences } = useSearch(
    ExtendedDocumentReference,
    ExtendedDocumentReference.byPredictedRelation(orderReferenceString),
    { query: { enabled: !!orderReferenceString } }
  )

  const explicitResults = useMemo(
    () =>
      uniqBy(
        compact([
          ...(diagnosticReportDocumentReferences ?? []),
          ...(documentReferences ?? []),
          ...(supportingInfoDocumentReferenceIds.length > 0
            ? (supportingInfoDocumentReferences ?? [])
            : []),
        ]),
        (d) => d.id
      ),
    [
      diagnosticReportDocumentReferences,
      documentReferences,
      supportingInfoDocumentReferenceIds,
      supportingInfoDocumentReferences,
    ]
  )

  const suggestedResults = useMemo(() => {
    const explicitIds = new Set(explicitResults.map((d) => d.id))

    return (predictedDocumentReferences ?? [])
      .filter((doc) => {
        // Only include documents that have a predicted relation to this order
        // and are NOT already explicitly linked
        if (explicitIds.has(doc.id)) {
          return false
        }
        const predictedOrders = (doc.predictedOrders ?? []).filter(
          (pred) => getReferenceString(pred.reference) === orderReferenceString
        )
        const result = predictedOrders.some((pred) => {
          return pred.confidence > 0.9 && !!pred.reason
        })
        return result
      })
      .sort((a, b) => {
        // Sort by confidence descending
        const aOrder = a.predictedOrders?.find(
          (pred) => getReferenceString(pred.reference) === orderReferenceString
        )
        const bOrder = b.predictedOrders?.find(
          (pred) => getReferenceString(pred.reference) === orderReferenceString
        )
        return (bOrder?.confidence ?? 0) - (aOrder?.confidence ?? 0)
      })
  }, [predictedDocumentReferences, orderReferenceString, explicitResults])

  return {
    explicit: explicitResults,
    suggested: suggestedResults.filter((d) => !d.orders?.length),
  }
}
